/**
 * CriticalPathSection — SCM5.
 *
 * Renders the critical-path items computed by `computeCriticalPath`. The
 * component is pure-presentational — it takes a `CriticalPathResult` and
 * never recomputes; the parent owns recomputation. That keeps the
 * negative-slack rendering aligned with the rest of the panel
 * (same result instance everywhere).
 *
 * Highlighting rules (mirroring the SCM5 spec):
 *   - 🔴 / `bg-error/10` row when `isOverdue` (`slack < 0`)
 *   - 🟡 / `bg-warning/10` row when `0 ≤ slack < 3 mo`
 *   - default styling for items NOT on the critical path (rendered only
 *     when the user expands "Show all" — keeps the default view focused).
 */
import React, { useState } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import type { CriticalPathItem, CriticalPathResult } from '../../../lib/criticalPathAnalysis';
import { CRITICAL_PATH_SLACK_THRESHOLD_MONTHS } from '../../../lib/criticalPathAnalysis';
import { cn } from '../../../lib/utils';
import { SCM_CATEGORY_LABELS } from '../../../types/supplyChain';

export interface CriticalPathSectionProps {
  result: CriticalPathResult;
}

/** Round-half-away-from-zero helper that keeps the sign explicit. */
function slackLabel(months: number): string {
  if (!Number.isFinite(months)) return '—';
  const rounded = Math.round(months);
  if (rounded === 0) return '0 mo';
  return rounded > 0 ? `+${rounded} mo` : `${rounded} mo`;
}

function rowTone(item: CriticalPathItem): { row: string; pill: string; label: string } {
  if (item.isOverdue) {
    return {
      row: 'border-error/40 bg-error/10',
      pill: 'bg-error text-white',
      label: 'OVERDUE',
    };
  }
  if (item.isUserFlagged && item.isScheduleAtRisk) {
    return {
      row: 'border-warning/40 bg-warning/10',
      pill: 'bg-warning text-warning-foreground',
      label: 'FLAGGED + AT RISK',
    };
  }
  if (item.isUserFlagged) {
    return {
      row: 'border-primary/40 bg-primary/10',
      pill: 'bg-primary text-white',
      label: 'FLAGGED',
    };
  }
  if (item.isScheduleAtRisk) {
    return {
      row: 'border-warning/40 bg-warning/10',
      pill: 'bg-warning text-warning-foreground',
      label: 'AT RISK',
    };
  }
  return {
    row: 'border-border bg-background',
    pill: 'bg-muted text-text-secondary',
    label: 'OK',
  };
}

export const CriticalPathSection: React.FC<CriticalPathSectionProps> = ({ result }) => {
  const [showAll, setShowAll] = useState(false);
  const codMissing = result.inServiceDate == null;
  const itemsToShow = showAll ? result.items : result.criticalPathItems;
  const allCount = result.items.length;
  const cpCount = result.criticalPathItems.length;
  const flaggedOnly = result.userFlaggedItems.length;

  if (codMissing && cpCount === 0) {
    return (
      <div
        className="border border-dashed border-border bg-muted p-4 type-body text-text-secondary"
        data-testid="critical-path-cod-missing"
      >
        <div className="flex items-center gap-2 type-section text-text-muted">
          <Clock className="w-3 h-3" /> Critical path
        </div>
        <p className="mt-2">
          Set the project in-service date (COD) in Project Intent or Financial
          Assumptions to activate lead-time risk scheduling. Items with negative
          slack will surface here.
        </p>
      </div>
    );
  }

  if (codMissing && flaggedOnly > 0) {
    return (
      <section
        className="border border-border bg-background p-4 space-y-3"
        data-testid="critical-path-section"
        aria-label="Critical path"
      >
        <header>
          <div className="flex items-center gap-2 type-section text-text-muted">
            <Clock className="w-3 h-3" /> Critical path — designated
          </div>
          <p className="type-body text-text-secondary mt-1">
            {flaggedOnly} item{flaggedOnly === 1 ? '' : 's'} flagged on the BoM.
            Set COD to add schedule-at-risk analysis.
          </p>
        </header>
        <ul className="space-y-1" data-testid="critical-path-list">
          {result.userFlaggedItems.map((item) => {
            const tone = rowTone(item);
            return (
              <li
                key={item.bomItemId}
                className={cn('flex items-center gap-3 px-3 py-2 border', tone.row)}
                data-testid={`critical-path-item-${item.bomItemId}`}
                data-user-flagged="true"
              >
                <span className={cn('px-2 py-0.5 type-meta font-bold uppercase tracking-widest', tone.pill)}>
                  {tone.label}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold text-foreground truncate">{item.name}</div>
                  <div className="type-body text-text-secondary">
                    {SCM_CATEGORY_LABELS[item.category]} · lead {item.leadTimeMonthsHigh} mo
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  if (allCount === 0) {
    return null;
  }

  return (
    <section
      className="border border-border bg-background p-4 space-y-3"
      data-testid="critical-path-section"
      aria-label="Critical path"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 type-section text-text-muted">
            <Clock className="w-3 h-3" /> Critical path
          </div>
          <p className="type-body text-text-secondary mt-1">
            Items with less than {CRITICAL_PATH_SLACK_THRESHOLD_MONTHS} months of slack vs the COD
            target. Negative slack = already late on order.
          </p>
        </div>
        {result.hasOverdueItems && (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 bg-error text-white type-body font-bold uppercase tracking-widest"
            data-testid="critical-path-overdue-banner"
          >
            <AlertTriangle className="w-3 h-3" />
            {result.totalNegativeSlackMonths.toFixed(0)} mo behind across {result.items.filter(i => i.isOverdue).length} items
          </span>
        )}
      </header>

      {cpCount === 0 && (
        <p
          className="type-body text-success rounded-md border border-success/40 bg-success/10 px-3 py-2"
          data-testid="critical-path-clear"
        >
          No items on the critical path — every BoM item has &gt;
          {CRITICAL_PATH_SLACK_THRESHOLD_MONTHS} months of slack vs the COD target.
        </p>
      )}

      {itemsToShow.length > 0 && (
        <ul className="space-y-1" data-testid="critical-path-list">
          {itemsToShow.map((item) => {
            const tone = rowTone(item);
            return (
              <li
                key={item.bomItemId}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 border',
                  tone.row,
                )}
                data-testid={`critical-path-item-${item.bomItemId}`}
                data-overdue={item.isOverdue ? 'true' : 'false'}
                data-on-critical-path={item.isOnCriticalPath ? 'true' : 'false'}
              >
                <span
                  className={cn(
                    'px-2 py-0.5 type-meta font-bold uppercase tracking-widest',
                    tone.pill,
                  )}
                >
                  {tone.label}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold text-foreground truncate">
                    {item.name}
                  </div>
                  <div className="type-body text-text-secondary">
                    {SCM_CATEGORY_LABELS[item.category]} · lead {item.leadTimeMonthsHigh} mo
                    {item.latestOrderDate && (
                      <> · order-by {new Date(item.latestOrderDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}</>
                    )}
                  </div>
                </div>
                <span
                  className={cn(
                    'text-[10px] font-bold tabular-nums',
                    item.isOverdue ? 'text-error' : item.isOnCriticalPath ? 'text-warning' : 'text-text-secondary',
                  )}
                  data-testid={`critical-path-slack-${item.bomItemId}`}
                >
                  {slackLabel(item.slackMonths)}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {allCount > cpCount && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="type-body font-bold uppercase tracking-widest text-primary hover:underline"
          data-testid="critical-path-show-all"
        >
          {showAll
            ? `Show only critical (${cpCount})`
            : `Show all items (${allCount})`}
        </button>
      )}
    </section>
  );
};
